export const APPEARANCE_MODES = Object.freeze({
    SYSTEM: 'system',
    LIGHT: 'light',
    DARK: 'dark',
});

export const APPEARANCE_STORAGE_KEY = 'appearance_mode';

const allowedModes = new Set(Object.values(APPEARANCE_MODES));
let currentAppearanceMode = APPEARANCE_MODES.SYSTEM;

export const normalizeAppearanceMode = mode => {
    const normalized = typeof mode === 'string' ? mode.trim() : '';
    return allowedModes.has(normalized) ? normalized : APPEARANCE_MODES.SYSTEM;
};

export const getSystemAppearance = () => {
    if (
        typeof window !== 'undefined' &&
        typeof window.matchMedia === 'function' &&
        window.matchMedia('(prefers-color-scheme: dark)').matches
    ) {
        return APPEARANCE_MODES.DARK;
    }
    return APPEARANCE_MODES.LIGHT;
};

export const resolveAppearanceMode = mode => {
    const normalized = normalizeAppearanceMode(mode);
    return normalized === APPEARANCE_MODES.SYSTEM
        ? getSystemAppearance()
        : normalized;
};

export const applyAppearanceMode = mode => {
    const normalized = normalizeAppearanceMode(mode);
    currentAppearanceMode = normalized;

    if (typeof document !== 'undefined') {
        const root = document.documentElement;
        root.setAttribute('data-appearance-mode', normalized);
        root.setAttribute(
            'data-resolved-appearance',
            resolveAppearanceMode(normalized)
        );
    }

    return normalized;
};

export const readStoredAppearanceMode = () => {
    if (typeof localStorage === 'undefined') {
        return APPEARANCE_MODES.SYSTEM;
    }
    return normalizeAppearanceMode(
        localStorage.getItem(APPEARANCE_STORAGE_KEY)
    );
};

export const writeStoredAppearanceMode = mode => {
    const normalized = normalizeAppearanceMode(mode);
    if (typeof localStorage !== 'undefined') {
        localStorage.setItem(APPEARANCE_STORAGE_KEY, normalized);
    }
    return normalized;
};

export const initializeAppearanceMode = () => {
    const storedMode = readStoredAppearanceMode();
    applyAppearanceMode(storedMode);
    return storedMode;
};

export const syncAppearanceMode = (mode, { persist = true } = {}) => {
    const normalized = applyAppearanceMode(mode);
    if (persist) {
        writeStoredAppearanceMode(normalized);
    }
    return normalized;
};

const handleSystemPreferenceChange = () => {
    if (currentAppearanceMode === APPEARANCE_MODES.SYSTEM) {
        applyAppearanceMode(currentAppearanceMode);
    }
};

export const watchSystemAppearancePreference = () => {
    if (
        typeof window === 'undefined' ||
        typeof window.matchMedia !== 'function'
    ) {
        return () => {};
    }

    const mediaQueryList = window.matchMedia('(prefers-color-scheme: dark)');

    if (typeof mediaQueryList.addEventListener === 'function') {
        mediaQueryList.addEventListener('change', handleSystemPreferenceChange);
        return () => {
            mediaQueryList.removeEventListener(
                'change',
                handleSystemPreferenceChange
            );
        };
    }

    if (typeof mediaQueryList.addListener === 'function') {
        mediaQueryList.addListener(handleSystemPreferenceChange);
        return () => {
            mediaQueryList.removeListener(handleSystemPreferenceChange);
        };
    }

    return () => {};
};
